import React from 'react';
import { Button, Dialog, Group, Spinner, useToast } from '@economic/taco';
import { t, Trans } from '@lingui/macro';

import { Form } from '~/app/shared/components/Form';
import DimensionLineCombobox from '~/app/shared/dimensions/components/DimensionLineCombobox';
import internalApiClient from '~/app/shared/util/api/internalApiClient';
import { Dimension } from '~/app/shared/dimensions/types';
import { useLocalization } from '~/app/shared/components/l10n/l10n';
import { dimensionsApiErrorTextIds } from '~/app/shared/dimensions/api/other';
import { getAPIErrorValidation } from '~/utilities/parseAPIValidationErrors';
import { BulkDimensionRequest } from '../types';

interface BulkDimensionFormProps {
    initialData: Omit<BulkDimensionRequest, 'dimensionData'>;
    dimensions: Dimension[];
    onSubmitted: () => void;
    setLoading: (loading: boolean) => void;
    navigateBack: () => void;
}

export const BulkDimensionForm = ({ initialData, dimensions, onSubmitted, setLoading, navigateBack }: BulkDimensionFormProps) => {
    const toast = useToast();
    const textsLoading = useLocalization(dimensionsApiErrorTextIds);

    const onSubmit = async ({ draftId, selectedLineIds, dimensionData }: BulkDimensionRequest & { dimensionData: number[] }) => {
        const request: BulkDimensionRequest = {
            draftId,
            selectedLineIds,
            dimensionData: dimensionData.reduce((acc, val, index) => {
                if (val) {
                    acc[index] = val;
                }
                return acc;
            }, {}),
        };

        setLoading(true);
        try {
            await internalApiClient.post('/sales/invoicing/bulk-dimension', request);

            toast.success(t`Dimension(s) updated`);
            navigateBack();
            onSubmitted();
        } catch (error) {
            toast.error(getAPIErrorValidation(error)?.[''] ?? t`Something went wrong`, { autoClose: 10000 });
        }
        setLoading(false);
    };

    const clearDimensions = async () => {
        setLoading(true);
        try {
            const request: BulkDimensionRequest = initialData;
            await internalApiClient.post('/sales/invoicing/bulk-dimension', request);

            toast.success(t`Dimension(s) updated`);
            navigateBack();
            onSubmitted();
        } catch (error) {
            toast.error(t`Something went wrong`, { autoClose: 10000 });
        }

        setLoading(false);
    };

    if (textsLoading) {
        return (
            <Dialog.Content aria-label={t`Loading`}>
                <Spinner className="w-full h-full flex justify-center" />
            </Dialog.Content>
        );
    }

    return (
        <Form initialData={initialData} onSubmit={onSubmit} skipNotifications>
            {dimensions
                ? dimensions.map(dim => (
                      <DimensionLineCombobox
                          key={dim.id}
                          data={dim}
                          dimensionId={dim.id}
                          name={`dimensionData.${dim.id}`}
                          label={dim.name}
                          enableCreate={false}
                      />
                  ))
                : null}

            <Dialog.Footer className="justify-between">
                <Button appearance="ghost" onClick={clearDimensions}>
                    <Trans>Clear dimensions</Trans>
                </Button>
                <Group>
                    <Button appearance="discrete" onClick={navigateBack}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button appearance="primary" type="submit">
                        <Trans>OK</Trans>
                    </Button>
                </Group>
            </Dialog.Footer>
        </Form>
    );
};
